import { ReactNode, createContext, useEffect, useMemo, useState } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { AccountDetailedDto, isAccountValid } from '@model/AccountDetailedDto'

import { Locale } from '@global/locale/Locale'
import { useGuardedContext } from '@global/useGuardedContext'

import { safeLocalStorage } from '@hooks/useStorage'

interface AccountReadContextImpl {
  account?: AccountDetailedDto
  email?: string
}

interface AccountWriteContextImpl {
  refreshAccount: (locale: Locale) => Promise<AccountDetailedDto | undefined>
  fetchAccount: (locale: Locale) => Promise<AccountDetailedDto | undefined>
  updateAccount: (account: AccountDetailedDto) => void
  clearAccount: () => void
}

const AccountReadContext = createContext<AccountReadContextImpl | undefined>(undefined)
AccountReadContext.displayName = 'AccountReadContext'

const AccountWriteContext = createContext<AccountWriteContextImpl | undefined>(undefined)
AccountWriteContext.displayName = 'AccountWriteContext'

export function useAccountReadContext(): AccountReadContextImpl {
  return useGuardedContext(AccountReadContext)
}

export function useAccountWriteContext(): AccountWriteContextImpl {
  return useGuardedContext(AccountWriteContext)
}

interface Props {
  account?: AccountDetailedDto
  children: ReactNode
}

export function AccountContextProvider({ account, children }: Props): React.ReactElement<any> {
  const [accountState, setAccountState] = useState(account)
  // const updateContext = useUnleashContext()

  useEffect(() => {
    setAccountState(account)
  }, [account])

  const apiClient = useApiClient(ClientApiClient)

  const readContext = useMemo<AccountReadContextImpl>(
    () => ({
      email: accountState?.emails?.find((email) => email.isPrimary)?.address,
      account: accountState,
    }),
    [accountState]
  )

  const writeContext = useMemo<AccountWriteContextImpl>(() => {
    const updateAccount = (newAccount: AccountDetailedDto) => {
      if (!isAccountValid(newAccount)) return
      safeLocalStorage.setJSON('account', newAccount)
      setAccountState(newAccount)
    }

    return {
      async refreshAccount(locale) {
        try {
          const accountWithAppTests = await apiClient.getAccountWithAppTestResults(locale)
          updateAccount(accountWithAppTests)
          return accountWithAppTests
        } catch (e: unknown) {
          console.error('Error during refreshAccount', e)
        }
      },

      async fetchAccount(locale) {
        try {
          return await apiClient.getAccountWithAppTestResults(locale)
        } catch (e: unknown) {
          console.error('Error during fetchAccount', e)
        }
      },

      updateAccount,

      clearAccount() {
        safeLocalStorage.removeItem('account')
        setAccountState(undefined)
      },
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountState])

  return (
    <AccountReadContext.Provider value={readContext}>
      <AccountWriteContext.Provider value={writeContext}>{children}</AccountWriteContext.Provider>
    </AccountReadContext.Provider>
  )
}

export const initiateAccountContext = (): AccountDetailedDto | undefined => {
  const account = safeLocalStorage.getJSON<unknown>('account')
  if (account === null) return undefined

  if (!isAccountValid(account)) {
    safeLocalStorage.removeItem('account')
    return undefined
  }

  return account
}
